import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { getSession } from "@/api/auth";
import * as authApi from "@/api/auth";

export function useAuth() {
  const navigate = useNavigate();
  const {
    user,
    tokens,
    isAuthenticated,
    initialized,
    signIn: storeSignIn,
    signOut: storeSignOut,
    setSession,
    initialize,
  } = useAuthStore();

  // On mount, check sessionStorage for existing session and verify server-side
  useEffect(() => {
    if (!initialized) {
      initialize();
    }
  }, [initialized, initialize]);

  // Verify server-side session validity when we have local tokens
  useEffect(() => {
    if (initialized && isAuthenticated) {
      getSession().then((session) => {
        if (!session) {
          storeSignOut();
          navigate("/signin", { replace: true });
        }
      }).catch(() => {
        // Network errors are okay — keep the local session
      });
    }
  }, [initialized]); // only run once after init

  const signIn = useCallback(
    async (email: string, password: string) => {
      const { user, tokens } = await authApi.signIn(email, password);
      storeSignIn(user, tokens);
      navigate("/", { replace: true });
    },
    [navigate, storeSignIn],
  );

  const devSignIn = useCallback(async () => {
    const { user, tokens } = await authApi.devLogin();
    storeSignIn(user, tokens);
    navigate("/", { replace: true });
  }, [navigate, storeSignIn]);

  const signUp = useCallback(
    async (email: string, password: string, name?: string) => {
      const { user, tokens } = await authApi.signUp(email, password, name);
      storeSignIn(user, tokens);
      navigate("/", { replace: true });
    },
    [navigate, storeSignIn],
  );

  const signOut = useCallback(async () => {
    try {
      await authApi.signOut();
    } catch {
      // Proceed with local sign-out even if server call fails
    }
    storeSignOut();
    navigate("/signin", { replace: true });
  }, [navigate, storeSignOut]);

  return {
    user,
    tokens,
    isAuthenticated,
    initialized,
    signIn,
    devSignIn,
    signUp,
    signOut,
  };
}